import { useState, useEffect } from "react";
import { db } from "./firebase";
import { collection, getDocs, query, orderBy, limit, doc, getDoc, setDoc } from "firebase/firestore";
import { dedupeByArtwork } from "./utils";

const CHARTS = [
  { id: "global", name: "Global Top 50", flag: "🌍", color: "#1db954", bg: "linear-gradient(135deg, #0d2818, #000)", q: "top hits 2024" },
  { id: "us", name: "US Charts", flag: "🇺🇸", color: "#4776e6", bg: "linear-gradient(135deg, #0d1a38, #000)", q: "usa top hits 2024" },
  { id: "uk", name: "UK Charts", flag: "🇬🇧", color: "#e91429", bg: "linear-gradient(135deg, #2a0808, #000)", q: "uk top hits 2024" },
{ id: "myanmar", name: "Myanmar Top", flag: "🇲🇲", color: "#f59e0b", bg: "linear-gradient(135deg, #2a1a00, #000)", q: "myanmar song" },
  { id: "india", name: "India Top", flag: "🇮🇳", color: "#f97316", bg: "linear-gradient(135deg, #2a1000, #000)", q: "india top songs 2024" },
  { id: "kpop", name: "K-Pop Charts", flag: "🇰🇷", color: "#8b5cf6", bg: "linear-gradient(135deg, #1a0d2e, #000)", q: "kpop hits 2024" },
  { id: "pop", name: "Pop Charts", flag: "🎵", color: "#ec4899", bg: "linear-gradient(135deg, #2a0818, #000)", q: "pop hits 2024" },
  { id: "hiphop", name: "Hip Hop", flag: "🎤", color: "#6d28d9", bg: "linear-gradient(135deg, #1a0d2e, #000)", q: "hip hop hits 2024" },
  { id: "rnb", name: "R&B Charts", flag: "💜", color: "#4776e6", bg: "linear-gradient(135deg, #0d1a38, #000)", q: "rnb hits 2024" },
  { id: "christian", name: "Christian", flag: "✝️", color: "#3b82f6", bg: "linear-gradient(135deg, #0d1a38, #000)", q: "christian music 2024" },
  { id: "rock", name: "Rock Charts", flag: "🎸", color: "#ef4444", bg: "linear-gradient(135deg, #2a0808, #000)", q: "rock hits 2024" },
  { id: "latin", name: "Latin Charts", flag: "💃", color: "#f59e0b", bg: "linear-gradient(135deg, #2a1a00, #000)", q: "latin hits 2024" },
];

export default function Charts({ onPlaySong, currentSong }) {
  const [selectedChart, setSelectedChart] = useState(null);
  const [songs, setSongs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [myMusicChart, setMyMusicChart] = useState([]);
const [myMusicLoading, setMyMusicLoading] = useState(true);
useEffect(() => {
  async function loadMyMusicChart() {
    try {
      const q = query(collection(db, "songs"), orderBy("plays", "desc"), limit(20));
      const snap = await getDocs(q);
      const songs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setMyMusicChart(songs);
    } catch {}
    setMyMusicLoading(false);
  }
  loadMyMusicChart();
}, []);

 
async function loadChart(chart) {
    setSelectedChart(chart);
    setSongs([]);
    setLoading(true);

    const cacheKey = `chart_${chart.id}`;

    // 1. Check Firestore shared cache (refreshes every 6 hours)
    try {
      const cacheDoc = await getDoc(doc(db, "chartsCache", cacheKey));
      if (cacheDoc.exists()) {
        const { results, cachedAt } = cacheDoc.data();
        if (Date.now() - new Date(cachedAt).getTime() < 6 * 3600000 && results.length > 0) {
          setSongs(results);
          setLoading(false);
          return;
        }
      }
    } catch {}

    // 2. Fetch fresh from iTunes
    try {
      const r = await fetch(`https://itunes.apple.com/search?term=${encodeURIComponent(chart.q)}&media=music&limit=50`);
      const d = await r.json();
     const results = dedupeByArtwork(d.results || []);
      setSongs(results);

      // 3. Save to shared Firestore cache
      if (results.length > 0) {
        try {
          await setDoc(doc(db, "chartsCache", cacheKey), {
            results,
            chartId: chart.id,
            cachedAt: new Date().toISOString()
          });
        } catch {}
      }
    } catch {
      setSongs([]);
    }
    setLoading(false);
  }

  if (selectedChart) {
    return (
      <div style={{ minHeight: "100vh", background: "#000", color: "#fff", fontFamily: "'DM Sans',sans-serif", paddingBottom: 120 }}>
        <style>{`@import url('https://fonts.googleapis.com/css2?family=Syne:wght@700;800&family=DM+Sans:wght@400;500;600&display=swap');`}</style>

        {/* Header */}
        <div style={{ background: selectedChart.bg, padding: "24px 16px 20px" }}>
          <button onClick={() => setSelectedChart(null)} style={{ background: "rgba(0,0,0,0.3)", border: "none", color: "#fff", width: 34, height: 34, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", marginBottom: 16 }}>
            <svg viewBox="0 0 24 24" style={{ width: 14, height: 14, fill: "none", stroke: "#fff", strokeWidth: 2 }}><polyline points="15 18 9 12 15 6"/></svg>
          </button>
          <div style={{ fontSize: 32, marginBottom: 8 }}>{selectedChart.flag}</div>
          <div style={{ fontFamily: "'Syne',sans-serif", fontSize: 26, fontWeight: 800, letterSpacing: "-0.5px", marginBottom: 4 }}>{selectedChart.name}</div>
          <div style={{ fontSize: 13, color: "rgba(255,255,255,0.5)" }}>Updated daily · {songs.length} songs</div>
        </div>

        {/* Songs */}
        {loading ? (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "48px 0", gap: 12 }}>
            <div style={{ width: 32, height: 32, border: "3px solid #222", borderTopColor: "#1db954", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            <p style={{ color: "#555", fontSize: 13 }}>Loading chart...</p>
          </div>
        ) : songs.map((song, i) => {
          const playing = currentSong?.trackId === song.trackId;
          return (
            <div key={song.trackId} onClick={() => onPlaySong(song, songs)}
              style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 16px", cursor: "pointer", background: playing ? "rgba(29,185,84,0.06)" : "transparent", borderLeft: playing ? "3px solid #1db954" : "3px solid transparent" }}>
              
              {/* Rank */}
              <div style={{ width: 28, textAlign: "center", flexShrink: 0 }}>
                {playing ? (
                  <div style={{ display: "flex", gap: 2, alignItems: "flex-end", justifyContent: "center", height: 16 }}>
                    <div style={{ width: 3, background: "#1db954", height: "6px", borderRadius: 2, animation: "eq1 0.8s ease infinite alternate" }} />
                    <div style={{ width: 3, background: "#1db954", height: "10px", borderRadius: 2, animation: "eq2 0.8s ease infinite alternate" }} />
                    <div style={{ width: 3, background: "#1db954", height: "14px", borderRadius: 2, animation: "eq3 0.8s ease infinite alternate" }} />
                  </div>
                ) : (
                  <span style={{ fontFamily: "'Syne',sans-serif", fontSize: i < 3 ? 16 : 13, fontWeight: i < 3 ? 800 : 400, color: i === 0 ? "#ffd700" : i === 1 ? "#c0c0c0" : i === 2 ? "#cd7f32" : "#444" }}>
                    {i + 1}
                  </span>
                )}
              </div>

              {/* Artwork */}
              <img src={song.artworkUrl100} alt="" loading="lazy" style={{ width: 48, height: 48, borderRadius: 8, objectFit: "cover", flexShrink: 0, background: "#111" }} />

              {/* Info */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: playing ? 700 : 500, color: playing ? "#1db954" : "#fff", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", marginBottom: 3 }}>{song.trackName}</div>
                <div style={{ fontSize: 12, color: "#555", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{song.artistName}</div>
              </div>

              {/* Play button */}
              <div style={{ width: 36, height: 36, borderRadius: "50%", background: playing ? "#1db954" : "#111", border: "1px solid #2a2a2a", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                {playing ? (
                  <svg viewBox="0 0 24 24" style={{ width: 14, height: 14, fill: "#000" }}><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg>
                ) : (
                  <svg viewBox="0 0 24 24" style={{ width: 14, height: 14, fill: "#fff" }}><polygon points="5 3 19 12 5 21 5 3"/></svg>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", background: "#000", color: "#fff", fontFamily: "'DM Sans',sans-serif", paddingBottom: 120 }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Syne:wght@700;800&family=DM+Sans:wght@400;500;600&display=swap');
        @keyframes eq1 { from{height:6px} to{height:14px} }
        @keyframes eq2 { from{height:10px} to{height:6px} }
        @keyframes eq3 { from{height:14px} to{height:10px} }
      `}</style>

      {/* Header */}
      <div style={{ padding: "24px 16px 20px", background: "linear-gradient(160deg, #0d2818 0%, #000 70%)" }}>
        <div style={{ fontFamily: "'Syne',sans-serif", fontSize: 28, fontWeight: 800, letterSpacing: "-0.5px", marginBottom: 6 }}>📊 Charts</div>
        <div style={{ fontSize: 13, color: "#666" }}>Top songs from around the world</div>
      </div>

      {/* MyMusic Real-time Chart */}
<div style={{ margin: "0 16px 20px" }}>
  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
    <div>
      <div style={{ fontFamily: "'Syne',sans-serif", fontSize: 17, fontWeight: 800 }}>🔥 MyMusic Charts</div>
      <div style={{ fontSize: 11, color: "#555", marginTop: 2 }}>Most played on MyMusic · Live data</div>
    </div>
    <div style={{ display: "flex", alignItems: "center", gap: 6, background: "rgba(29,185,84,0.1)", border: "1px solid rgba(29,185,84,0.2)", borderRadius: 50, padding: "4px 10px" }}>
      <div style={{ width: 6, height: 6, borderRadius: "50%", background: "#1db954" }} />
      <span style={{ fontSize: 11, color: "#1db954", fontWeight: 600 }}>LIVE</span>
    </div>
  </div>

  {myMusicLoading ? (
    <div style={{ display: "flex", justifyContent: "center", padding: "24px 0" }}>
      <div style={{ width: 24, height: 24, border: "2px solid #222", borderTopColor: "#1db954", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
    </div>
  ) : myMusicChart.length === 0 ? (
    <div style={{ background: "#111", borderRadius: 12, padding: 20, textAlign: "center", border: "1px solid #1a1a1a" }}>
      <div style={{ fontSize: 32, marginBottom: 8 }}>🎵</div>
      <div style={{ fontSize: 13, color: "#555" }}>No songs yet — be the first to upload!</div>
    </div>
  ) : myMusicChart.map((song, i) => {
    const playing = currentSong?.trackId === song.id;
    return (
      <div key={song.id} onClick={() => onPlaySong({ trackId: song.id, trackName: song.title, artistName: song.artistName, artworkUrl100: song.coverUrl || "", previewUrl: "", isOriginal: true, audioUrl: song.audioUrl }, myMusicChart)}
        style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 0", borderBottom: "1px solid #0a0a0a", cursor: "pointer" }}>
        <div style={{ width: 28, textAlign: "center", flexShrink: 0 }}>
          {playing ? (
            <div style={{ display: "flex", gap: 2, alignItems: "flex-end", justifyContent: "center", height: 16 }}>
              <div style={{ width: 3, background: "#1db954", height: "6px", borderRadius: 2, animation: "eq1 0.8s ease infinite alternate" }} />
              <div style={{ width: 3, background: "#1db954", height: "10px", borderRadius: 2, animation: "eq2 0.8s ease infinite alternate" }} />
              <div style={{ width: 3, background: "#1db954", height: "14px", borderRadius: 2, animation: "eq3 0.8s ease infinite alternate" }} />
            </div>
          ) : (
            <span style={{ fontFamily: "'Syne',sans-serif", fontSize: i < 3 ? 16 : 13, fontWeight: i < 3 ? 800 : 400, color: i === 0 ? "#ffd700" : i === 1 ? "#c0c0c0" : i === 2 ? "#cd7f32" : "#444" }}>
              {i + 1}
            </span>
          )}
        </div>
        <div style={{ width: 46, height: 46, borderRadius: 8, background: song.coverUrl ? "transparent" : "linear-gradient(135deg,#1db954,#0a8f3c)", overflow: "hidden", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", border: playing ? "2px solid #1db954" : "none" }}>
          {song.coverUrl ? <img src={song.coverUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <span style={{ fontSize: 20 }}>🎵</span>}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: playing ? 700 : 500, color: playing ? "#1db954" : "#fff", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{song.title}</div>
          <div style={{ fontSize: 12, color: "#555", marginTop: 2 }}>{song.artistName}</div>
        </div>
        <div style={{ textAlign: "right", flexShrink: 0 }}>
          <div style={{ fontSize: 12, color: "#1db954", fontWeight: 600 }}>{(song.plays || 0).toLocaleString()}</div>
          <div style={{ fontSize: 10, color: "#444" }}>plays</div>
        </div>
      </div>
    );
  })}
</div>
      {/* Featured chart */}
      <div onClick={() => loadChart(CHARTS[0])} style={{ margin: "0 16px 16px", borderRadius: 16, padding: "20px", background: CHARTS[0].bg, cursor: "pointer", border: "1px solid rgba(255,255,255,0.06)" }}>
        <div style={{ fontSize: 36, marginBottom: 8 }}>{CHARTS[0].flag}</div>
        <div style={{ fontFamily: "'Syne',sans-serif", fontSize: 22, fontWeight: 800, marginBottom: 4 }}>{CHARTS[0].name}</div>
        <div style={{ fontSize: 13, color: "rgba(255,255,255,0.5)", marginBottom: 14 }}>The most played songs globally right now</div>
        <div style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "#1db954", borderRadius: 50, padding: "8px 16px" }}>
          <svg viewBox="0 0 24 24" style={{ width: 14, height: 14, fill: "#000" }}><polygon points="5 3 19 12 5 21 5 3"/></svg>
          <span style={{ fontSize: 13, fontWeight: 700, color: "#000" }}>Play All</span>
        </div>
      </div>

      {/* Chart grid */}
      <div style={{ padding: "0 16px" }}>
        <div style={{ fontFamily: "'Syne',sans-serif", fontSize: 17, fontWeight: 800, marginBottom: 14 }}>All Charts</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          {CHARTS.slice(1).map(chart => (
            <div key={chart.id} onClick={() => loadChart(chart)}
              style={{ borderRadius: 14, padding: "16px 14px", background: chart.bg, cursor: "pointer", border: "1px solid rgba(255,255,255,0.05)" }}>
              <div style={{ fontSize: 28, marginBottom: 8 }}>{chart.flag}</div>
              <div style={{ fontFamily: "'Syne',sans-serif", fontSize: 14, fontWeight: 700, color: chart.color }}>{chart.name}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
